import { GlobalWizardState, LOBState } from "stores/wizard/state";
import { action, computed, observable } from "mobx";
import { VrConfig } from "stores/wizard/config";
import { TypeaheadSelection } from "uitk-react-typeahead";
import { Child, Room, Travelers } from "src/components/shared/TravelersField/typings";
import { travelersMetadata } from "src/components/shared/TravelersField/utils";
import { LastSearchData, VRLastSearchData } from "stores/wizard/LastSearchData";

export class VrWizardState implements LOBState {
  private globalState: GlobalWizardState;

  public wizardInput: React.RefObject<HTMLInputElement>;

  @computed
  public get canTrack() {
    return this.globalState.canTrack;
  }

  public get isDesktop() {
    return this.globalState.isDesktop;
  }

  @computed
  public get travelersValueChanged() {
    return this.globalState.travelersValueChanged;
  }

  @action
  public setTravelersValue = () => {
    this.globalState.setTravelersValue();
  };

  public lastSearchData?: VRLastSearchData;

  @computed
  public get location() {
    return this.globalState.location;
  }

  @computed
  public get travelers() {
    const nonHotelRoom: [Room] = [
      {
        adults: 2,
        children: [],
        infants: [],
      },
    ];

    // Loop through existing room Info for travelers to find applicable room
    const applicableRoomsForTravelers: Room[] = this.globalState.travelers.hotel.rooms.map(
      (room: Room, roomIndex: number) => {
        const applicableAdultsPerRoom =
          room.adults > this.config.travelers.maxAdultsPerRoom ? this.config.travelers.maxAdultsPerRoom : room.adults;

        const combinedChildren = room.children.concat(room.infants);
        const applicableChildrenPerRoom =
          combinedChildren.length > this.config.travelers.maxChildrenPerRoom
            ? combinedChildren.splice(0, this.config.travelers.maxChildrenPerRoom)
            : combinedChildren;

        return {
          adults: applicableAdultsPerRoom,
          children: applicableChildrenPerRoom,
          infants: [],
        };
      }
    );

    // Only the first room details to be preserved in nonHotel [Room]
    nonHotelRoom[0] = applicableRoomsForTravelers[0];

    const vrTravelers = {
      hotel: {
        rooms: applicableRoomsForTravelers,
        infantSeating: this.globalState.travelers.hotel.infantSeating,
      },
      hotelPackage: {
        rooms: applicableRoomsForTravelers,
        infantSeating: this.globalState.travelers.hotelPackage.infantSeating,
      },
      nonHotel: {
        rooms: nonHotelRoom,
        infantSeating: this.globalState.travelers.nonHotel.infantSeating,
      },
      cruise: {
        rooms: nonHotelRoom,
        infantSeating: this.globalState.travelers.nonHotel.infantSeating,
      },
    };

    return vrTravelers;
  }

  @computed
  public get hotelTravelersMetadata() {
    return travelersMetadata(this.travelers.hotel);
  }

  @computed
  public get cruiseTravelersMetadata() {
    return travelersMetadata(this.travelers.cruise);
  }

  @observable
  public config: VrConfig;

  @observable
  public subLOBState = "";

  //Since this LOB doesn't have a subLOB, setting this as empty so the queryParam is removed when navigating to this LOB
  @action
  public updateSubLOBState(subLOBState: string) {
    this.subLOBState = "";
  }

  @computed
  public get date() {
    return this.globalState.date;
  }

  // validations
  @observable
  public destinationInvalidKey: string | "";

  @action
  public validateDestinationField = () => {
    if (!this.globalState.validateDestinationField()) {
      this.destinationInvalidKey = this.config.location.destination.invalidLocationMessageKey;
      this.wizardInput?.current?.focus();

      return false;
    }

    this.destinationInvalidKey = "";

    return true;
  };

  @observable
  public hotelTravelersInvalidKey = "";

  @action
  public validateLessThanNTravelers = () => {
    const isValid = this.globalState.validateLessThanNTravelers(this.hotelTravelersMetadata, this.config.travelers);
    if (!isValid) {
      this.hotelTravelersInvalidKey = this.config.travelers.invalidLessThanNTravelersMessageToken;

      return false;
    }

    return true;
  };

  @action
  public validateChildInfantFields = () => {
    if (this.config.travelers.withRooms && !this.globalState.validateChildInfantFields(this.travelers)) {
      this.hotelTravelersInvalidKey = this.config.travelers.invalidChildInfantValuesMessageToken!;
      return false;
    }
    return true;
  };

  @action
  public validateTravelersField = () => this.validateLessThanNTravelers() && this.validateChildInfantFields();

  @observable
  public dateStartInvalidKey = "";

  @observable
  public dateEndInvalidKey = "";

  @action
  public validateMaxDateRange = () => {
    if (!this.globalState.validateMaxDateRange(this.date.start, this.date.end, this.config.date.maxDaysRange)) {
      this.dateEndInvalidKey = this.config.date.invalidMaxDatesRangeMessageToken;

      return false;
    }

    return true;
  };

  @action
  public validateEndDateField = () => this.validateMaxDateRange();

  @action
  public resetValidations = () => {
    this.destinationInvalidKey = "";
    this.hotelTravelersInvalidKey = "";
    this.dateStartInvalidKey = "";
    this.dateEndInvalidKey = "";
  };

  public validateForm = () => {
    const validations: boolean[] = [
      this.validateDestinationField(),
      this.validateTravelersField(),
      this.validateEndDateField(),
    ];

    return !validations.includes(false);
  };

  // other actions
  @action
  public overrideConfig(callback: () => void): void {
    callback();
  }

  @action
  public updateDateSelection = (start: Date, end: Date) => {
    this.globalState.updateDateSelection(start, end);
    this.dateStartInvalidKey = "";
    this.dateEndInvalidKey = "";
  };

  @action
  public updateDestinationSelection = (selection: TypeaheadSelection) => {
    this.globalState.updateDestinationSelection(selection);
    this.destinationInvalidKey = "";
  };

  public submit = (event: React.FormEvent) => {
    const isValidForm = this.validateForm();
    if (!isValidForm) {
      event.preventDefault();
    }
  };

  @action
  public updateHotelTravelersSelection = (travelers: Travelers) => {
    const rooms = travelers.rooms.map((room) => {
      const [roomInfants, roomChildren] = room.children.reduce(
        ([infants, children]: [Child[], Child[]], child) => {
          child.age < 2 ? infants.push(child) : children.push(child);

          return [infants, children];
        },
        [[], []]
      );

      return {
        adults: room.adults,
        children: roomChildren,
        infants: roomInfants,
      };
    });
    this.globalState.updateHotelTravelersSelection({
      rooms,
      infantSeating: travelers.infantSeating,
    });
    this.hotelTravelersInvalidKey = "";
  };

  public updateDateFromConfig = () => {
    this.globalState.updateDateFromConfig(this.config.date);
  };

  constructor(config: VrConfig, globalState: GlobalWizardState, lastSearchData?: LastSearchData) {
    this.config = config;
    this.globalState = globalState;
  }
}
